// src/components/BookingCalendar.js
//
// Month-view booking calendar for the public provider page — the full
// replacement for the week-timetable AvailabilityGrid (Ivan's call,
// 2026-08-26, overriding the earlier do-not-deviate ruling; layout
// adapted from a sketch he supplied, rebuilt on real data + tokens).
//
// Invariants this component MUST keep (they're the page's truth model):
//   - NO start times before the address. A day tap fires onOpenDay and
//     nothing else — the DaySheet owns confirm-day → address → curated
//     chips. No time chips ever render here.
//   - Same props contract as the old grid, so PublicProviderProfile's
//     CTA state machine (selectedDate highlight, captionOverride,
//     ribbon) works unchanged.
//   - Venue tabs scope the data exactly as before (`venue` param on
//     week-view).
//   - /week-view caps ranges at 14 days (server-enforced), so a month
//     loads as three parallel chunks merged into one map, cached per
//     month+venue for the session.
//   - Under the legend, when a day is selected, one sentence says that
//     day's open windows by kind ("Ivan is available from 9:00am –
//     11:30am for in-home appointments and from 2:00pm – 5:00pm for
//     appointments at Peters Chiropractic"; Ivan, 2026-10-28, replacing
//     the swipeable timeline). READ-ONLY: never a picker, never time
//     chips. It reads the month's already-fetched days map (`open`).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { DateTime } from 'luxon';
// ONE definition of the availability-kind colors, shared with
// MonthCalendar's cells and CalendarSection's legend — the tints here
// and everywhere else must never drift apart (Ivan, 2026-08-27).
import { KIND_COLORS } from '../shared/kindColors';

// ── Pure helpers (exported for tests) ───────────────────────────────

function toMin(hhmm) {
  if (typeof hhmm !== 'string') return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

// Merge overlapping windows within a single day — the raw response can
// return two adjacent Availability rows that should read as one open
// window. (Ported verbatim from the retired AvailabilityGrid.)
export function mergeWindows(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) return [];
  const sorted = blocks
    .map(b => ({ startMin: toMin(b.start), endMin: toMin(b.end === '24:00' ? '23:59' : b.end), kind: b.kind }))
    .filter(b => b.startMin != null && b.endMin != null && b.endMin > b.startMin)
    .sort((a, b) => a.startMin - b.startMin);
  const out = [];
  for (const b of sorted) {
    const prev = out[out.length - 1];
    if (prev && b.startMin <= prev.endMin) {
      prev.endMin = Math.max(prev.endMin, b.endMin);
    } else {
      out.push({ ...b });
    }
  }
  return out;
}

// Total OPEN hours in a day — decides whether a cell is an "open day"
// (the bible's Available state) or the filled No-availability state.
export function openHoursFor(day) {
  const merged = mergeWindows(day?.availability || []);
  const mins = merged.reduce((n, w) => n + (w.endMin - w.startMin), 0);
  return mins / 60;
}

// TODAY's remaining open minutes — a day whose every window has
// already ended must not render as an open cell (Regina's report:
// morning windows kept today "Available" at 11 AM). Window HH:mm are
// provider-local; nowMin comes from the viewer's clock — a cross-zone
// visitor drifts by the zone delta, the same known limitation the
// sheet's day-nav guard carries. minuteOfDay injectable for tests.
export function remainingMinutesToday(day, nowMinuteOfDay) {
  const merged = mergeWindows(day?.availability || []);
  return merged.reduce(
    (n, w) => n + Math.max(0, w.endMin - Math.max(w.startMin, nowMinuteOfDay)),
    0
  );
}

// Which availability kinds a day offers. Flexible contributes to BOTH
// signals — the day is either-or, which the gold tint communicates
// (same derivation as MonthCalendar's cells).
export function kindTintFor(day) {
  const kinds = new Set();
  for (const b of day?.availability || []) kinds.add(b.kind || 'mobile');
  if (kinds.size === 0) return null;
  const flex = kinds.has('flexible');
  const hasMobile = kinds.has('mobile') || flex;
  const hasStatic = kinds.has('static') || flex;
  if (hasMobile && hasStatic) return KIND_COLORS.bothFill;
  return hasMobile ? KIND_COLORS.mobileFill : KIND_COLORS.studioFill;
}

// The spoken version of the tint — cells carry the word, not just the
// color (Ivan, 2026-08-31). Same derivation as kindTintFor.
export function kindLabelFor(day) {
  const kinds = new Set();
  for (const b of day?.availability || []) kinds.add(b.kind || 'mobile');
  if (kinds.size === 0) return null;
  const flex = kinds.has('flexible');
  const hasMobile = kinds.has('mobile') || flex;
  const hasStatic = kinds.has('static') || flex;
  if (hasMobile && hasStatic) return 'both';
  return hasMobile ? 'in-home' : 'in-studio';
}

// The 7×6 cell grid for a month — real Date arithmetic, so it cannot
// duplicate or skip a date. Returns ISO date strings.
export function buildMonthCells(year, month /* 1-12 */) {
  const first = DateTime.local(year, month, 1);
  const startWeekday = first.weekday % 7; // Sunday-first: Sun=0 … Sat=6
  const cells = [];
  for (let i = 0; i < 42; i++) {
    cells.push(first.minus({ days: startWeekday }).plus({ days: i }).toFormat('yyyy-MM-dd'));
  }
  return cells;
}

// Chunk a month's visible range into ≤14-day spans for /week-view.
export function chunkRanges(fromISO, toISO, maxDays = 14) {
  const out = [];
  let cur = DateTime.fromISO(fromISO);
  const end = DateTime.fromISO(toISO);
  while (cur <= end) {
    const chunkEnd = DateTime.min(cur.plus({ days: maxDays - 1 }), end);
    out.push({ from: cur.toFormat('yyyy-MM-dd'), to: chunkEnd.toFormat('yyyy-MM-dd') });
    cur = chunkEnd.plus({ days: 1 });
  }
  return out;
}

// Caption explains what tapping DOES; the legend explains what the
// tint IS. In month view the tap target is the DAY (only open days
// are tappable), so "open day" is accurate here — unlike the old band
// grid, where the block was the target.
function captionFor({ isSignedIn, savedAddress, venueName }) {
  if (venueName) {
    return (
      <>
        Tap an open day to pick a start time at{' '}
        <span className="text-ink">{venueName}</span>.
      </>
    );
  }
  if (isSignedIn && savedAddress) {
    return (
      <>
        Tap an open day to pick a start time at{' '}
        <span className="text-ink">{savedAddress}</span>.
      </>
    );
  }
  return (
    <>
      Tap an open day to get started. For in-home appointments,
      you&rsquo;ll be asked for your address first — start times account
      for the drive.
    </>
  );
}

// Styling bible: /home/ivan/Downloads/BookingCalendar.jsx (Ivan,
// 2026-08-27) — palette, serif stack, radii, cell states copied
// verbatim. Logic stays ours (real data, no times before address).
const COLORS = {
  card: '#f8f4ea',
  text: '#4a3323',
  muted: '#9c8b78',
  accent: '#b8672f',
  accentText: '#fdf8f1',
  unavailable: '#ddd2c1',
  unavailableText: '#b3a896',
  border: '#e2d7c5',
};
const SERIF = '"Iowan Old Style","Palatino Linotype",Palatino,Georgia,serif';
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// ── The day's open windows, one card per place (2026-10-28) ─────────
// Ivan replaced the swipeable day timeline with the day's windows of
// availability, then picked concept A, "Places and times", of canvas
// EXPNdobK27BqfrhprmeKdf: a card per place (its icon in the calendar's
// kind tint) saying "Ivan has availability from 9:00am to 10:00am, and
// 6:30pm to 8:00pm." Windows, never slots: no time is a button (the
// time buttons read as single appointments). The windows are the day's
// `open` from /week-view: the one open-times engine's answer
// (getAvailabilityWindows), so a time this says is open is one the
// booking flow will offer. Bookings, calendar blocks and past time are
// already out, and nothing about who booked is in it.

// "9:00am" from "09:00", the way Ivan writes it.
function clockWord(hhmm) {
  const m = toMin(hhmm);
  if (m == null) return '';
  const h24 = Math.floor(m / 60) % 24;
  return `${((h24 + 11) % 12) + 1}:${String(m % 60).padStart(2, '0')}${h24 >= 12 ? 'pm' : 'am'}`;
}

// One card per place: what it's called, its tint, and its windows.
export function openWindowPlaces(open) {
  if (!open) return [];
  const spans = (ws) => (ws || []).map((w) => ({ from: clockWord(w.start), to: clockWord(w.end) }));
  const places = [];
  if (open.mobile?.length) places.push({ kind: 'mobile', place: 'At your place', spans: spans(open.mobile) });
  for (const v of open.venues || []) {
    if (v.windows?.length) places.push({ kind: 'venue', place: v.name || 'In the studio', spans: spans(v.windows) });
  }
  for (const e of open.either || []) {
    if (e.windows?.length) {
      places.push({
        kind: 'either',
        place: e.name ? `At your place or ${e.name}` : 'At your place or in the studio',
        spans: spans(e.windows),
      });
    }
  }
  return places;
}

// The card's words as plain text: "Ivan has availability from 9:00am
// to 10:00am, and 6:30pm to 8:00pm."
export function openWindowLine(spans, firstName) {
  if (!spans?.length) return '';
  const lead = firstName ? `${firstName} has availability` : 'Availability';
  const said = spans.map((s) => `${s.from} to ${s.to}`);
  const list = said.length === 1 ? said[0] : `${said.slice(0, -1).join(', ')}, and ${said[said.length - 1]}`;
  return `${lead} from ${list}.`;
}

const PLACE_LOOK = {
  mobile: { tint: KIND_COLORS.mobileFill, Icon: Home },
  venue: { tint: KIND_COLORS.studioFill, Icon: Building2 },
  either: { tint: KIND_COLORS.bothFill, Icon: Home },
};

function PlaceCard({ place, firstName }) {
  const { tint, Icon } = PLACE_LOOK[place.kind];
  const time = (t) => <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{t}</span>;
  const n = place.spans.length;
  return (
    <div style={{ background: '#fbf7ef', border: '1px solid rgba(42,37,32,0.12)', borderRadius: 14, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span aria-hidden="true" style={{ width: 36, height: 36, flex: 'none', borderRadius: 10, background: tint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} strokeWidth={1.6} color={COLORS.text} />
        </span>
        <span className="text-base" style={{ fontWeight: 600, color: COLORS.text }}>{place.place}</span>
      </div>
      <p className="text-[15px] leading-relaxed" style={{ color: COLORS.text, margin: 0, marginTop: 10 }}>
        {firstName ? `${firstName} has availability` : 'Availability'} from{' '}
        {place.spans.map((s, i) => (
          <React.Fragment key={i}>
            {i > 0 && (n > 1 && i === n - 1 ? ', and ' : ', ')}
            {time(s.from)} to {time(s.to)}
          </React.Fragment>
        ))}
        .
      </p>
    </div>
  );
}

function DayWindows({ dateISO, day, firstName }) {
  const dt = DateTime.fromISO(dateISO);
  const places = openWindowPlaces(day?.open);
  const offered = (day?.availability || []).length > 0;
  const today = dateISO === DateTime.now().toFormat('yyyy-MM-dd');
  let words = null;
  if (!places.length && day?.open && offered) {
    words = today
      ? <>No more openings today &mdash; pick another date.</>
      : <>{firstName ? `${firstName} is booked up` : 'Booked up'} this day &mdash; pick another date.</>;
  } else if (!places.length && !offered) {
    words = <>No availability this day &mdash; pick another date.</>;
  }

  return (
    <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${COLORS.border}` }}>
      <p style={{ fontFamily: SERIF, color: COLORS.text, margin: 0 }} className="text-lg">
        {dt.toFormat('ccc, LLL d')}
      </p>
      {words && (
        <p className="mt-1.5 text-[15px] leading-relaxed" style={{ color: COLORS.text, margin: 0, marginTop: 6 }}>
          {words}
        </p>
      )}
      {places.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
          {places.map((p, i) => <PlaceCard key={i} place={p} firstName={firstName} />)}
        </div>
      )}
    </div>
  );
}

export default function BookingCalendar({
  joinCode,
  providerFirstName,
  isSignedIn,
  savedAddress,
  venue,
  venueName,
  onOpenDay,
  selectedDate = null,
  captionOverride = null,
  // Wizard embedding (2026-08-29): the booking wizard reuses this
  // exact component for its date step. It hides the public page's
  // caption + sign-in tease and can force a refetch after
  // availability mutations via refreshKey.
  showCaption = true,
  refreshKey = 0,
}) {
  const today = DateTime.now().toFormat('yyyy-MM-dd');
  const [viewMonth, setViewMonth] = useState(() => DateTime.now().startOf('month'));
  const [days, setDays] = useState({});       // date → { availability, busy }
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  // Session cache: `${yyyy-MM}|${venue}` → days map. A ref, not state —
  // it never drives a render on its own.
  const cacheRef = useRef({});

  const monthKey = `${viewMonth.toFormat('yyyy-MM')}|${venue || 'all'}|${refreshKey}`;

  useEffect(() => {
    if (!joinCode) return;
    const cached = cacheRef.current[monthKey];
    if (cached) { setDays(cached); setErr(''); return; }
    let cancelled = false;
    setLoading(true);
    setErr('');
    const from = viewMonth.toFormat('yyyy-MM-dd');
    const to = viewMonth.endOf('month').toFormat('yyyy-MM-dd');
    const venueParam = venue ? `&venue=${venue}` : '';
    Promise.all(
      chunkRanges(from, to).map(r =>
        fetch(`/api/public/${joinCode}/week-view?from=${r.from}&to=${r.to}${venueParam}`)
          .then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      )
    )
      .then(chunks => {
        if (cancelled) return;
        const merged = {};
        for (const json of chunks) {
          const d = json?.days || {};
          const entries = Array.isArray(d)
            ? d.map(x => [x.date, x])
            : Object.entries(d);
          for (const [date, v] of entries) merged[date] = v;
        }
        cacheRef.current[monthKey] = merged;
        setDays(merged);
      })
      .catch(e => { if (!cancelled) setErr(e.message || 'Failed to load availability'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [joinCode, monthKey, viewMonth, venue]);

  const cells = useMemo(
    () => buildMonthCells(viewMonth.year, viewMonth.month),
    [viewMonth]
  );

  const atCurrentMonth = viewMonth.toFormat('yyyy-MM') === DateTime.now().toFormat('yyyy-MM');

  return (
    <div
      className="av-cal-card w-full"
      style={{ background: COLORS.card, borderRadius: 28, boxShadow: '0 1px 3px rgba(74,51,35,0.1)', padding: 24 }}
    >
      {/* Header — nav circles flanking the serif month title */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => setViewMonth(m => m.minus({ months: 1 }))}
          disabled={atCurrentMonth}
          aria-label="Previous month"
          className="av-cal-nav rounded-full flex items-center justify-center disabled:opacity-40"
          style={{ width: 36, height: 36, border: `1px solid ${COLORS.border}`, color: COLORS.text, background: 'transparent', cursor: atCurrentMonth ? 'default' : 'pointer' }}
        >
          <ChevronLeft size={18} />
        </button>
        <h2 className="av-cal-month" style={{ fontFamily: SERIF, fontSize: 24, color: COLORS.text, margin: 0 }}>
          {viewMonth.toFormat('LLLL yyyy')}
        </h2>
        <button
          type="button"
          onClick={() => setViewMonth(m => m.plus({ months: 1 }))}
          aria-label="Next month"
          className="av-cal-nav rounded-full flex items-center justify-center"
          style={{ width: 36, height: 36, border: `1px solid ${COLORS.border}`, color: COLORS.text, background: 'transparent', cursor: 'pointer' }}
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="mb-4" style={{ borderTop: `1px solid ${COLORS.text}`, opacity: 0.5 }} />

      {err ? (
        <div className="py-6 text-sm" style={{ color: 'var(--danger)' }}>{err}</div>
      ) : (
        <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 120ms ease' }}>
          {/* Day labels */}
          <div className="grid grid-cols-7 mb-2">
            {DOW.map((d, i) => (
              <div key={i} className="text-center text-xs font-semibold tracking-wide" style={{ color: COLORS.text }}>
                {d}
              </div>
            ))}
          </div>
          {/* Date cells — the bible's four states */}
          <div className="grid grid-cols-7 gap-1.5">
            {cells.map(date => {
              const dt = DateTime.fromISO(date);
              const outside = dt.month !== viewMonth.month;
              const past = date < today;
              const day = days[date];
              const isTodayCell = date === today;
              // Today opens on REMAINING window time, not raw windows —
              // an exhausted today (all windows ended) renders as the
              // beige unavailable state instead of inviting a tap into
              // an empty sheet.
              const nowDT = DateTime.now();
              const open = !past && !outside && day
                ? (isTodayCell
                    ? remainingMinutesToday(day, nowDT.hour * 60 + nowDT.minute) > 0
                    : openHoursFor(day) > 0)
                : false;
              const isSelected = selectedDate === date && !outside;
              const isToday = date === today;

              let style = {
                fontFamily: SERIF,
                color: COLORS.text,
                background: 'transparent',
                border: `1px solid ${COLORS.border}`,
              };
              if (outside) {
                style = { ...style, color: COLORS.muted, border: '1px solid transparent', opacity: 0.55 };
              } else {
                if (!open) {
                  style = { ...style, background: COLORS.unavailable, color: COLORS.unavailableText, border: '1px solid transparent' };
                } else {
                  // Open cells wear their availability kind: sage =
                  // mobile, slate = in-studio, gold = both/flexible.
                  const tint = kindTintFor(day);
                  if (tint) style = { ...style, background: tint };
                }
                if (isToday && !isSelected) {
                  style = { ...style, border: `1.5px solid ${COLORS.text}` };
                }
              }
              if (isSelected) {
                style = { ...style, background: COLORS.accent, color: COLORS.accentText, border: '1px solid transparent' };
              }

              return (
                <button
                  key={date}
                  type="button"
                  onClick={() => open && onOpenDay && onOpenDay({ date })}
                  disabled={!open}
                  aria-label={open ? `Open day (${kindLabelFor(day) || 'available'}), ${dt.toFormat('cccc, LLLL d')}` : dt.toFormat('cccc, LLLL d')}
                  aria-pressed={isSelected}
                  className="av-cal-cell aspect-square rounded-xl flex items-center justify-center text-sm"
                  style={{ ...style, cursor: open ? 'pointer' : 'not-allowed' }}
                >
                  {/* Tint only (Ivan, 2026-09-01) — the kind is carried
                      by color plus the legend, not by a word in the
                      tile. kindLabelFor still names it for screen
                      readers via aria-label above. */}
                  {dt.day}
                </button>
              );
            })}
          </div>

          {/* Legend — kind tints (shared KIND_COLORS) + the unavailable
              state. ONE LINE on phones (Ivan, 2026-08-30): compact
              text/swatches and "Both" for the mixed day; the selected
              day is self-evident (copper cell) so it isn't listed. */}
          <div
            className="flex flex-nowrap items-center justify-center mt-5 gap-x-2.5 sm:gap-x-5 text-[11.5px] sm:text-sm whitespace-nowrap"
            style={{ color: COLORS.text }}
          >
            {[
              ['Mobile', KIND_COLORS.mobileFill, true],
              ['In-studio', KIND_COLORS.studioFill, true],
              ['Both', KIND_COLORS.bothFill, true],
              ['Unavailable', COLORS.unavailable, false],
            ].filter(([label]) => {
              // A scoped calendar only ever shows its own kind (plus
              // gold "both" days), so the legend drops the entry that
              // can no longer appear: venue='mobile' hides In-studio,
              // a venue id hides Mobile (concierge wizard, 2026-09-01).
              if (venue === 'mobile') return label !== 'In-studio';
              if (venue) return label !== 'Mobile';
              return true;
            }).map(([label, bg, bordered]) => (
              <span key={label} className="flex items-center gap-1.5">
                <span
                  className="w-3.5 h-3.5 sm:w-5 sm:h-5 rounded-md flex-shrink-0"
                  style={{ background: bg, border: bordered ? `1px solid ${COLORS.border}` : 'none' }}
                />
                {label}
              </span>
            ))}
          </div>

          {/* Day timeline — read-only at-a-glance view of the selected
              day, drawn from the same days map the cells use. Renders
              only when the selected day's month is the one loaded. */}
          {selectedDate && days[selectedDate] && (
            <DayWindows dateISO={selectedDate} day={days[selectedDate]} firstName={providerFirstName} />
          )}
        </div>
      )}

      {/* Caption + sign-in tease — kept functional, dressed in the palette */}
      {showCaption && (
      <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${COLORS.border}` }}>
        <p className="av-cal-caption" style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 13, lineHeight: 1.5, color: COLORS.text, margin: 0 }}>
          {captionOverride || captionFor({ isSignedIn, savedAddress, venueName })}
        </p>
        {/* Sign-in tease removed (2026-08-29): the sticky CTA carries
            the Return-client sign-in door now. */}
      </div>
      )}
    </div>
  );
}

